import type { PageRangeMode } from '@/types/index';

/**
 * Parses a simple range such as:
 *
 * "1-5"   -> { from: 1, to: 5 }
 * " 2 - 8 " -> { from: 2, to: 8 }
 *
 * Returns null for anything that is not a simple range.
 */
export function parseSimpleRange(
  value: string,
): { from: number; to: number } | null {
  const match = value.trim().match(/^(\d+)\s*-\s*(\d+)$/);

  if (!match) {
    return null;
  }

  const from = Number(match[1]);
  const to = Number(match[2]);

  if (!Number.isInteger(from) || !Number.isInteger(to)) {
    return null;
  }

  if (from < 1 || to < from) {
    return null;
  }

  return {
    from,
    to,
  };
}

/**
 * Converts a page selection expression into a unique,
 * sorted list of page numbers.
 *
 * Examples:
 *
 * "2,5,8,13"
 * -> [2, 5, 8, 13]
 *
 * "1-4,7,9"
 * -> [1, 2, 3, 4, 7, 9]
 *
 * "2,5,5,8"
 * -> [2, 5, 8]
 *
 * Pages outside the document are ignored.
 */
export function parsePageSelection(
  expression: string,
  totalPages: number,
): number[] {
  if (!expression.trim() || totalPages <= 0) {
    return [];
  }

  const pages = new Set<number>();

  const parts = expression
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  for (const part of parts) {
    /*
     * Range:
     *
     * 1-5
     * 10-15
     */
    if (part.includes('-')) {
      const range = parseSimpleRange(part);

      if (!range) {
        continue;
      }

      const from = Math.max(1, range.from);
      const to = Math.min(totalPages, range.to);

      if (from > to) {
        continue;
      }

      for (let page = from; page <= to; page += 1) {
        pages.add(page);
      }

      continue;
    }

    /*
     * Single page:
     *
     * 5
     * 10
     * 25
     */
    const page = Number(part);

    if (
      Number.isInteger(page) &&
      page >= 1 &&
      page <= totalPages
    ) {
      pages.add(page);
    }
  }

  return Array.from(pages).sort((a, b) => a - b);
}

/**
 * Returns the number of printable pages represented
 * by the current page-range configuration.
 */
export function pageCountForConfig(
  mode: PageRangeMode,
  totalPages: number,
  fromPage: number,
  toPage: number,
  rangeExpression: string,
): number {
  if (totalPages <= 0) {
    return 0;
  }

  /*
   * All pages
   */
  if (mode === 'all') {
    return totalPages;
  }

  /*
   * Custom Set / Random Pages
   *
   * Both use rangeExpression.
   *
   * Examples:
   *
   * Custom:
   * 1-4,7,9
   *
   * Random:
   * 2,5,8,13
   */
  const expression =
    rangeExpression || `${fromPage}-${toPage}`;

  return parsePageSelection(
    expression,
    totalPages,
  ).length;
}